import { PersonalInformation } from '../types/PersonalInformation';
import datejs from 'date-and-time';

export const validateFirstname = (firstname: string): string => {
    if (firstname.length === 0) {
        return 'Fornavn må fylles ut';
    }
    if (firstname.length > 50) {
        return 'Fornavn kan ikke ha mer enn 50 tegn';
    }
    return '';
};

export const validateLastname = (lastname: string): string => {
    if (lastname.length === 0) {
        return 'Etternavn må fylles ut';
    }
    if (lastname.length > 50) {
        return 'Etternavn kan ikke ha mer enn 50 tegn';
    }
    return '';
};

export const validatePhoneNumber = (value: string): string => {
    if (value.length === 0) {
        return 'Mobilnummer må fylles ut';
    }
    if (!value.replaceAll(' ', '').match(/^((0047)?|(\+47)?)[4|9]\d{7}$/)) {
        return 'Mobilnummeret er ikke gyldig';
    }
    return '';
};

export const validateEmail = (value: string): string => {
    if (value.length === 0) {
        return 'E-post må fylles ut';
    }
    if (value.length > 200) {
        return 'E-post kan ikke ha mer enn 200 tegn';
    }
    if (!value.match(/^\w+([.-]?\w+)*@\w+([.-]?\w+)*$/)) {
        return 'E-postadressen er ikke gyldig';
    }
    return '';
};

export const validateAddress = (value: string) => {
    if (value.length === 0) {
        return 'Adresse må fylles ut';
    }
    if (value.length > 100) {
        return 'Adresse kan ikke ha mer enn 100 tegn';
    }
    return '';
};

export const validatePostalcode = (value: string) => {
    if (value.length === 0) {
        return 'Postnummer må fylles ut';
    }
    if (!value.match(/^\d{4}$/)) {
        return 'Postnummer må ha 4 siffer';
    }
    return '';
};

export const validateDateOfBirth = (value: string) => {
    if (value.length === 0) {
        return 'Fødselsdato må fylles ut';
    }
    if (!value.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
        return 'Fødselsdato må skrives som dd.mm.åååå';
    }
    if (!datejs.isValid(value, 'DD.MM.YYYY')) {
        return 'Fødselsdato er ikke gyldig';
    }

    const isBefore1900 = datejs.subtract(datejs.parse(value, 'DD.MM.YYYY'), new Date(1900, 0, 1)).toDays() < 0;

    if (isBefore1900) {
        return 'Fødselsdato er ikke gyldig';
    }

    const isFutureDate = datejs.subtract(datejs.parse(value, 'DD.MM.YYYY'), new Date()).toDays() > 0;
    if (isFutureDate) {
        return 'Fødselsdato kan ikke være frem i tid';
    }

    return '';
};

export const validateDateOfBirthAndChildAge = (value: string) => {
    const dateErrormessage = validateDateOfBirth(value);
    if (dateErrormessage !== '') {
        return dateErrormessage;
    }
    const inputDate = datejs.parse(value, 'DD.MM.YYYY');

    const now = new Date();
    const earliestChildBirthDate = datejs.addYears(now, -26);

    if (inputDate <= earliestChildBirthDate) {
        return 'Barn må være under 26 år';
    }
    return '';
};
export const validateFreetext = (value: string | undefined) => {
    if (value?.length === 0) {
        return 'Hovedmedlem må fylles ut';
    }
    if (value && value.length > 100) {
        return 'Feltet kan ikke ha mer enn 100 tegn';
    }
    return '';
};

export const isValid = (personData: PersonalInformation): boolean => {
    const hasError =
        validateFirstname(personData.firstname) ||
        validateLastname(personData.lastname) ||
        validatePhoneNumber(personData.phoneNumber) ||
        validateEmail(personData.email) ||
        validateAddress(personData.address) ||
        validatePostalcode(personData.postalcode) ||
        validateDateOfBirth(personData.dateOfBirth);
    return !hasError;
};
export const isValidHusstand = (personData: PersonalInformation): boolean => {
    const hasError =
        validateFirstname(personData.firstname) ||
        validateLastname(personData.lastname) ||
        validatePhoneNumber(personData.phoneNumber) ||
        validateEmail(personData.email) ||
        validateAddress(personData.address) ||
        validatePostalcode(personData.postalcode) ||
        validateDateOfBirth(personData.dateOfBirth) ||
        validateFreetext(personData.freetext);
    return !hasError;
};

export const isValidOrEmptyPartner = (personData?: PersonalInformation): boolean => {
    if (personData == null) {
        return true;
    } else {
        return isValidPartner(personData);
    }
};

export const isValidPartner = (personData: PersonalInformation): boolean => {
    const hasError =
        validateFirstname(personData.firstname) ||
        validateLastname(personData.lastname) ||
        validatePhoneNumber(personData.phoneNumber) ||
        validateEmail(personData.email) ||
        validateDateOfBirth(personData.dateOfBirth);

    return !hasError;
};

export const isValidBarn = (personData: PersonalInformation): boolean => {
    const hasError =
        validateFirstname(personData.firstname) ||
        validateLastname(personData.lastname) ||
        validatePhoneNumber(personData.phoneNumber) ||
        validateEmail(personData.email) ||
        validateDateOfBirthAndChildAge(personData.dateOfBirth);

    return !hasError;
};

export const isValidOrEmptyBarneList = (personDataList?: PersonalInformation[]): boolean => {
    if (personDataList == null || personDataList.length === 0) {
        return true;
    }

    return !personDataList.some((personData) => {
        const hasError =
            validateFirstname(personData.firstname) ||
            validateLastname(personData.lastname) ||
            validatePhoneNumber(personData.phoneNumber) ||
            validateEmail(personData.email) ||
            validateDateOfBirthAndChildAge(personData.dateOfBirth);

        return hasError !== '';
    });
};
